import * as React from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/v2/components/ui/button";
import type { ContentItem, DragPayload } from "../types";
import type { TouchDragHandle } from "../useTouchDragBridge";
import { uploadErrorMessage } from "../uploadsApi";
import { useInfiniteGrid } from "../useInfiniteGrid";
import type { UploadsState } from "../useUploads";
import { ACCEPTED_VIDEO, UploadDialog } from "./UploadDialog";
import { ContentCard } from "./parts";

const STATUS_LABEL: Record<NonNullable<ContentItem["uploadStatus"]>, string> = {
  uploading: "Uploading",
  pending: "Queued",
  processing: "Processing",
  ready: "Ready",
  failed: "Failed",
};

function StatusChip({ status }: { status: NonNullable<ContentItem["uploadStatus"]> }) {
  const tone =
    status === "ready"
      ? "bg-emerald-100 text-emerald-800"
      : status === "failed"
        ? "bg-red-100 text-red-800"
        : "bg-amber-100 text-amber-800";
  return (
    <span className={`inline-flex items-center rounded-full text-xs font-medium ${tone}`} style={{ padding: "2px 8px", gap: 4 }}>
      {status === "pending" || status === "processing" ? <Loader2 size={11} className="animate-spin" /> : null}
      {STATUS_LABEL[status]}
    </span>
  );
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={Math.round(value * 100)}>
      <div className="h-full bg-action" style={{ width: `${Math.round(value * 100)}%`, transition: "width 200ms linear" }} />
    </div>
  );
}

interface CardProps {
  item: ContentItem;
  uploads: UploadsState;
  dragRef: React.MutableRefObject<DragPayload | null>;
  touchDrag?: TouchDragHandle;
}

/** One uploaded video: the regular library card (draggable only once ready)
 *  plus its status, live progress, and the actions its state allows. */
function UploadCard({ item, uploads, dragRef, touchDrag }: CardProps) {
  const assetId = item.id.replace(/^up-/, "");
  const local = uploads.local[assetId];
  const status = item.uploadStatus ?? "processing";
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const running = Boolean(local && (local.phase === "uploading" || local.phase === "completing"));
  const ready = status === "ready";

  const act = async (fn: () => Promise<void>, fallback: string) => {
    try {
      await fn();
    } catch (e) {
      toast.error(uploadErrorMessage(e, fallback));
    }
  };

  return (
    <div data-testid={`upload-card-${assetId}`}>
      <div className={ready ? undefined : "pointer-events-none opacity-60"}>
        <ContentCard item={item} dragRef={ready ? dragRef : undefined} touchDrag={ready ? touchDrag : undefined} />
      </div>
      <div className="flex flex-col" style={{ gap: 6, padding: "0 2px" }}>
        <div className="flex items-center justify-between" style={{ gap: 6 }}>
          <StatusChip status={running ? "uploading" : status} />
          {status === "uploading" && !running ? (
            <div className="flex" style={{ gap: 4 }}>
              <button
                type="button"
                className="text-xs font-medium text-action-hover hover:underline"
                onClick={() =>
                  void act(async () => {
                    // The stored copy first; the picker only when this
                    // browser has none (other browser, cleared data).
                    if (!(await uploads.retry(assetId))) fileRef.current?.click();
                  }, "Could not resume the upload")
                }
              >
                Resume
              </button>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-destructive"
                onClick={() => void act(() => uploads.cancel(assetId), "Could not cancel the upload")}
              >
                Cancel
              </button>
            </div>
          ) : running ? (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-destructive"
              onClick={() => void act(() => uploads.cancel(assetId), "Could not cancel the upload")}
            >
              Cancel
            </button>
          ) : status === "ready" || status === "failed" ? (
            <button
              type="button"
              aria-label={`Delete ${item.title}`}
              className="text-muted-foreground hover:text-destructive"
              onClick={() => void act(() => uploads.remove(assetId), "Could not delete the video")}
            >
              <Trash2 size={14} />
            </button>
          ) : null}
        </div>
        {running && local ? <ProgressBar value={local.fileSize ? local.loadedBytes / local.fileSize : 0} /> : null}
        {local?.phase === "error" ? (
          <p className="text-xs text-destructive">{local.error} It resumes when the connection is back, or press Resume.</p>
        ) : null}
        {status === "failed" && item.failureReason ? (
          <p className="text-xs text-destructive">{item.failureReason}</p>
        ) : null}
        {status === "uploading" && !running && local?.phase !== "error" ? (
          <p className="text-xs text-muted-foreground">Interrupted. Press Resume to continue.</p>
        ) : null}
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED_VIDEO}
          aria-label={`Resume ${item.title}`}
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void act(() => uploads.resume(assetId, file, item.title), "Could not resume the upload");
          }}
        />
      </div>
    </div>
  );
}


/** The Uploaded tab's header action: the Upload video button and its
 *  dialog. It takes the slot the Games tab gives its channel filter; the
 *  search box next to it is shared by both tabs. */
export function UploadVideoButton({ onStart }: { onStart: UploadsState["start"] }) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  return (
    <>
      <Button type="button" onClick={() => setDialogOpen(true)} className="bg-action text-white hover:bg-action-hover">
        <Plus size={16} />
        Upload video
      </Button>
      <UploadDialog open={dialogOpen} onOpenChange={setDialogOpen} onStart={onStart} />
    </>
  );
}

interface Props {
  uploads: UploadsState;
  search: string;
  dragRef: React.MutableRefObject<DragPayload | null>;
  touchDrag?: TouchDragHandle;
}

/** The Content Library's Uploaded tab grid (HTPTV2-89): the same two-row
 *  scroll window as the Games tab, loading the next page of uploads as it
 *  scrolls. Search and the upload button live in the library header. */
export function UploadedLibrary({ uploads, search, dragRef, touchDrag }: Props) {
  const { scrollRef, gridRef, sentinelRef, maxHeight } = useInfiniteGrid({
    count: uploads.items.length,
    hasMore: uploads.hasNextPage,
    loadingMore: uploads.isFetchingNextPage,
    onLoadMore: uploads.fetchNextPage,
  });

  if (uploads.isLoading) {
    return (
      <div className="flex justify-center" style={{ padding: "32px 0" }}>
        <Loader2 size={20} className="animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (uploads.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" style={{ padding: "32px 0", textAlign: "center" }}>
        {search ? "No uploads match your search." : "No uploaded videos yet. Upload one to schedule it."}
      </p>
    );
  }
  return (
    <div ref={scrollRef} className="overflow-y-auto" style={{ marginTop: 16, maxHeight }}>
      <div ref={gridRef} className="sched-cards" data-testid="uploads-grid">
        {uploads.items.map((item) => (
          <UploadCard key={item.id} item={item} uploads={uploads} dragRef={dragRef} touchDrag={touchDrag} />
        ))}
      </div>
      {uploads.hasNextPage ? (
        <div ref={sentinelRef} className="flex justify-center" style={{ marginTop: 16, height: 32 }}>
          {uploads.isFetchingNextPage ? (
            <Loader2 size={20} className="animate-spin text-muted-foreground" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
